import { useEffect, useState } from "react";
import {
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { type PaginationState, type SortingState } from "@tanstack/react-table";
import {
  api,
  type AppVersionDTO,
  type AppVersionsSortOptions,
} from "../../utils/api";
import { useDebounce } from "../../utils/useDebounce";
import { ErrorMessage } from "../../authorization/ErrorMessage";
import { AppVersionsTable } from "../tables/AppVersionsTable";
import { ForceUpdateField, isForceUpdateChecked } from "../ForceUpdateField";
import { toast } from "react-toastify";

export default function AppVersions({
  token,
  isAdmin,
}: {
  token: string;
  isAdmin: boolean;
}) {
  const [versions, setVersions] = useState<AppVersionDTO[]>([]);
  const [totalVersions, setTotalVersions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [editingVersion, setEditingVersion] = useState<AppVersionDTO | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<AppVersionDTO | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [dataTimestamp, setDataTimestamp] = useState(0);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "versionCode", desc: true },
  ]);

  const uploadApk = async (apkFile: File) => {
    const uploadFormData = new FormData();
    uploadFormData.append("file", apkFile);
    return api.uploadAppApk(token, uploadFormData).then((response) => response.data);
  };

  const getApiErrorMessage = (
    error: any,
    fallbackMessage: string
  ): string => {
    if (typeof error === "string") {
      if (error.includes("413 Request Entity Too Large")) {
        return "APK file is too large for upload.";
      }
      return error;
    }

    const responseData = error?.response?.data;
    if (typeof responseData === "string") {
      if (responseData.includes("413 Request Entity Too Large")) {
        return "APK file is too large for upload.";
      }
      return responseData;
    }

    return (
      error?.error ||
      responseData?.error ||
      error?.detail ||
      responseData?.detail ||
      error?.message ||
      responseData?.message ||
      error?.title ||
      responseData?.title ||
      fallbackMessage
    );
  };

  useEffect(() => {
    api
      .getAppVersions(
        token,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        sorting.length > 0
          ? ((sorting[0]?.desc
              ? `${sorting[0].id}_desc`
              : `${sorting[0].id}_asc`) as AppVersionsSortOptions)
          : undefined,
        debouncedSearchTerm || undefined
      )
      .then((body) => {
        setVersions(body.data);
        setTotalVersions(body.metadata.totalCount);
        setErrorMessage(
          !debouncedSearchTerm && body.data.length === 0
            ? "No app versions available."
            : ""
        );
      })
      .catch(() => {
        setErrorMessage("Failed to load app versions");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, pagination, sorting, debouncedSearchTerm, dataTimestamp]);

  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return (
    <div>
      {isAdmin ? (
        <div className="mb-3 d-flex justify-content-start">
          <Button variant="dark" onClick={() => setShowCreateModal(true)}>
            Create a new app version
          </Button>
        </div>
      ) : null}

      <InputGroup className="mb-3">
        <InputGroup.Text>
          <label htmlFor="search-app-versions" title="Search app versions">
            <FontAwesomeIcon icon={faSearch} />
          </label>
        </InputGroup.Text>
        <Form.Control
          id="search-app-versions"
          type="text"
          placeholder="Search app versions"
          value={searchTerm}
          maxLength={255}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      {(errorMessage && <ErrorMessage message={errorMessage} />) || (
        <AppVersionsTable
          versions={versions}
          totalVersions={totalVersions}
          showActions={isAdmin}
          {...(isAdmin
            ? {
                onEdit: (version: AppVersionDTO) => setEditingVersion(version),
                onDelete: (version: AppVersionDTO) => setDeletingVersion(version),
                onPublish: async (version: AppVersionDTO) => {
                  setIsSubmitting(true);
                  try {
                    await api.updateAppVersion(token, version.id, {
                      status: "PUBLISHED",
                    });
                    toast.success("App version published successfully.");
                    setDataTimestamp(Date.now());
                  } catch (error: any) {
                    toast.error(
                      getApiErrorMessage(error, "Failed to publish app version")
                    );
                  } finally {
                    setIsSubmitting(false);
                  }
                },
              }
            : {})}
          onTableChange={(nextPagination, nextSorting) => {
            setPagination(nextPagination);
            setSorting(nextSorting);
          }}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          sortBy={sorting?.[0]?.id}
          sortDescending={sorting?.[0]?.desc}
        />
      )}

      {isAdmin ? (
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create App Version</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={async (event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              setIsSubmitting(true);
              try {
                const apkFile = formData.get("apkFile");
                if (!(apkFile instanceof File) || apkFile.size === 0) {
                  toast.error("Please select an APK file.");
                  return;
                }
                if (!apkFile.name.toLowerCase().endsWith(".apk")) {
                  toast.error("Only .apk files are supported.");
                  return;
                }
                const uploadedApk = await uploadApk(apkFile);

                await api.createAppVersion(token, {
                  versionName: uploadedApk.versionName,
                  versionCode: uploadedApk.versionCode,
                  packageName: uploadedApk.packageName,
                  status: String(formData.get("status") || "DRAFT") as AppVersionDTO["status"],
                  forceUpdate: isForceUpdateChecked(formData),
                  downloadUrl: uploadedApk.downloadUrl,
                  releaseNotes: String(formData.get("releaseNotes") || ""),
                  sha256: uploadedApk.sha256,
                });
                toast.success("App version created successfully.");
                setShowCreateModal(false);
                setDataTimestamp(Date.now());
              } catch (error: any) {
                toast.error(getApiErrorMessage(error, "Failed to create app version"));
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>APK</Form.Label>
              <Form.Control name="apkFile" type="file" accept=".apk" required />
              <Form.Text muted>
                Version Name and Version Code are auto-derived from the uploaded APK.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select name="status" defaultValue="DRAFT">
                <option value="DRAFT">DRAFT</option>
                <option value="ARCHIVED">ARCHIVED</option>
                <option value="PUBLISHED">PUBLISHED</option>
              </Form.Select>
            </Form.Group>
            <Form.Control
              name="packageName"
              type="hidden"
              defaultValue={versions[0]?.packageName ?? ""}
            />
            <Form.Group className="mb-3">
              <Form.Label>Release Notes</Form.Label>
              <Form.Control name="releaseNotes" required />
            </Form.Group>
            <ForceUpdateField productLabel="app" />
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button variant="dark" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      ) : null}

      {isAdmin ? (
      <Modal show={!!editingVersion} onHide={() => setEditingVersion(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit App Version</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!editingVersion) {
                return;
              }

              const formData = new FormData(event.currentTarget);
              setIsSubmitting(true);
              try {
                await api.updateAppVersion(token, editingVersion.id, {
                  status: String(formData.get("status") || "DRAFT") as AppVersionDTO["status"],
                  releaseNotes: String(formData.get("releaseNotes") || ""),
                  forceUpdate: isForceUpdateChecked(formData),
                });
                toast.success("App version updated successfully.");
                setEditingVersion(null);
                setDataTimestamp(Date.now());
              } catch (error: any) {
                toast.error(getApiErrorMessage(error, "Failed to update app version"));
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>APK</Form.Label>
              <Form.Control value={editingVersion?.downloadUrl ?? ""} readOnly />
              <Form.Text muted>APK file cannot be changed after creation.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Version Name</Form.Label>
              <Form.Control value={editingVersion?.versionName ?? ""} readOnly />
              <Form.Text muted>
                Version Name is derived from the uploaded APK.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Version Code</Form.Label>
              <Form.Control value={editingVersion?.versionCode ?? ""} readOnly />
              <Form.Text muted>
                Version Code is derived from the uploaded APK.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                defaultValue={editingVersion?.status ?? "DRAFT"}
              >
                <option value="DRAFT">DRAFT</option>
                <option value="ARCHIVED">ARCHIVED</option>
                <option value="PUBLISHED">PUBLISHED</option>
              </Form.Select>
            </Form.Group>
            <Form.Control
              name="packageName"
              type="hidden"
              defaultValue={editingVersion?.packageName ?? ""}
            />
            <Form.Group className="mb-3">
              <Form.Label>Release Notes</Form.Label>
              <Form.Control
                name="releaseNotes"
                defaultValue={editingVersion?.releaseNotes}
                required
              />
            </Form.Group>
            <ForceUpdateField
              productLabel="app"
              defaultChecked={editingVersion?.forceUpdate ?? false}
            />
            <Form.Group className="mb-3">
              <Form.Label>SHA256</Form.Label>
              <Form.Control value={editingVersion?.sha256 ?? ""} readOnly />
              <Form.Text muted>
                SHA256 is auto-calculated from the uploaded APK and cannot be
                edited manually.
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setEditingVersion(null)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button variant="dark" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      ) : null}

      {isAdmin ? (
      <Modal show={!!deletingVersion} onHide={() => setDeletingVersion(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete App Version</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <code>{deletingVersion?.versionName}</code> (
          <code>{deletingVersion?.versionCode}</code>)?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setDeletingVersion(null)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={isSubmitting}
            onClick={async () => {
              if (!deletingVersion) {
                return;
              }
              setIsSubmitting(true);
              try {
                await api.deleteAppVersion(token, deletingVersion.id);
                toast.success("App version deleted successfully.");
                setDeletingVersion(null);
                setDataTimestamp(Date.now());
              } catch (error: any) {
                toast.error(getApiErrorMessage(error, "Failed to delete app version"));
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      ) : null}
    </div>
  );
}
